import { type MutableRefObject, useCallback, useRef, useState } from 'react'

import { createClient } from '@connectrpc/connect'
import { useTransport } from '@connectrpc/connect-query'
import { BiddingBidMarket } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { JepxSettlement } from '@ebalance/proto/bffuser/v1/jepx_settlement_model_pb'
import { JepxSettlementService } from '@ebalance/proto/bffuser/v1/jepx_settlement_service_pb'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { downloadBlob } from '~/libs/downloadBlob'
import { downloadCsv } from '~/libs/downloadCsv'
import {
  buildSettlementCsv,
  settlementFileBase,
} from '~/pages/JepxSettlement/models/settlementCsv'

type Download = {
  isPending: boolean
  download: () => Promise<void>
}

/**
 * 清算書 1 行分の PDF・CSV ダウンロード (AC-JST-006・007・008)。
 *
 * 押下時の命令的な呼び出しなので `useMutation` / `useQuery` は使わず、
 * `useTransport` + `createClient` で直接呼ぶ (前例: `useDownloadGenerationSalesPlanXml`)。
 * 失敗 (RPC・Blob・保存の例外) は PDF・CSV とも同じ案内を出し、 原因で出し分けない。
 * 処理中の状態は PDF と CSV で別々に持つ。
 */
export function useDownloadJepxSettlement(row: JepxSettlement): {
  pdf: Download
  csv: Download
} {
  const { t } = useTranslation()
  const transport = useTransport()
  const showErrorToast = useErrorToast()
  const [isPdfPending, setIsPdfPending] = useState(false)
  const [isCsvPending, setIsCsvPending] = useState(false)
  // 同じ tick の 2 回目の click では state がまだ古いので、 処理中は ref でも持って再入を止める。
  const isPdfRunning = useRef(false)
  const isCsvRunning = useRef(false)

  const run = useCallback(
    async (
      running: MutableRefObject<boolean>,
      setIsPending: (isPending: boolean) => void,
      task: () => Promise<void>,
    ): Promise<void> => {
      if (running.current) return
      running.current = true
      setIsPending(true)
      try {
        await task()
      } catch {
        showErrorToast({ title: t('page:jepxSettlement.error.downloadFailed') })
      } finally {
        running.current = false
        setIsPending(false)
      }
    },
    [showErrorToast, t],
  )

  // PDF・CSV 共通のファイル名の拡張子を除いた部分。 市場名の先頭語は表示言語に従う (AC-JST-006・007)。
  const fileBase = useCallback((): string => {
    switch (row.market) {
      case BiddingBidMarket.SPOT:
        return settlementFileBase(
          t('page:jepxSettlement.fileNamePrefix.spot'),
          row.title,
        )
      case BiddingBidMarket.INTRADAY:
        return settlementFileBase(
          t('page:jepxSettlement.fileNamePrefix.intraday'),
          row.title,
        )
      default:
        throw new Error('settlement row has no market')
    }
  }, [row, t])

  const downloadPdf = useCallback(
    () =>
      run(isPdfRunning, setIsPdfPending, async () => {
        const client = createClient(JepxSettlementService, transport)
        const { content } = await client.downloadJepxSettlementPdf({
          market: row.market,
          settlementNumber: row.settlementNumber,
        })
        downloadBlob(
          `${fileBase()}.pdf`,
          new Blob([new Uint8Array(content)], { type: 'application/pdf' }),
        )
      }),
    [fileBase, row, run, transport],
  )

  const downloadSummaryCsv = useCallback(
    () =>
      run(isCsvRunning, setIsCsvPending, async () => {
        const client = createClient(JepxSettlementService, transport)
        const { summary } = await client.getJepxSettlementSummary({
          market: row.market,
          settlementNumber: row.settlementNumber,
        })
        if (summary === undefined) {
          throw new Error('settlement summary is empty')
        }
        downloadCsv(`${fileBase()}.csv`, buildSettlementCsv(summary))
      }),
    [fileBase, row, run, transport],
  )

  return {
    pdf: { isPending: isPdfPending, download: downloadPdf },
    csv: { isPending: isCsvPending, download: downloadSummaryCsv },
  }
}
